import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import Navbar from "@/components/Navbar";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Calendar, Clock, MapPin, Users, ArrowLeft, UserPlus, FileText } from "lucide-react";

const CreateMatch = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showPositionDialog, setShowPositionDialog] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [createdMatchId, setCreatedMatchId] = useState(null);
  const [selectedPositions, setSelectedPositions] = useState([]);
  
  const [formData, setFormData] = useState({
    venueId: "",
    venueName: "",
    date: "",
    time: "",
    duration: 90,
    maxPlayers: 14,
    teamSize: 14
  });

  const calculatePrice = () => {
    if (!formData.venueId || !formData.duration) return 0;
    const venue = venues.find(v => v.id === formData.venueId);
    if (!venue || !venue.pricePerHour) return 0;
    return (venue.pricePerHour * (formData.duration / 60)).toFixed(2);
  };

  useEffect(() => {
    fetchVenues();
  }, []);

  const fetchVenues = async () => {
    try {
      const response = await axios.get(`${API}/venues`);
      setVenues(response.data);
    } catch (error) {
      console.error("Error fetching venues:", error);
      toast.error("Sahalar yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  // Get today's date in YYYY-MM-DD format
  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  const handleChange = (field, value) => {
    if (field === "venueId") {
      const selectedVenue = venues.find(v => v.id === value);
      setFormData(prev => ({
        ...prev,
        venueId: value,
        venueName: selectedVenue ? selectedVenue.name : ""
      }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.venueId || !formData.date || !formData.time) {
      toast.error("Lütfen tüm zorunlu alanları doldurun");
      return;
    }

    setCreating(true);
    try {
      const matchData = {
        ...formData,
        status: "open",
        captainId: user.id,
        captainName: user.name
      };

      const response = await axios.post(`${API}/matches`, matchData);
      setCreatedMatchId(response.data.id);
      toast.success("Maç başarıyla oluşturuldu!");
      setShowPositionDialog(true);
    } catch (error) {
      console.error("Error creating match:", error);
      toast.error(error.response?.data?.detail || "Maç oluşturulamadı");
    } finally {
      setCreating(false);
    }
  };

  const handleConfirmPositions = async () => {
    if (selectedPositions.length === 0) {
      toast.error("Lütfen en az bir pozisyon seçin");
      return;
    }

    try {
      // Create open positions for selected positions
      await axios.post(`${API}/matches/${createdMatchId}/open-positions`, {
        positions: selectedPositions
      });
      
      toast.success("Açık pozisyonlar oluşturuldu!");
      setShowPositionDialog(false);
      setShowSuccessDialog(true);
    } catch (error) {
      console.error("Error creating positions:", error);
      toast.error("Pozisyonlar oluşturulamadı");
    }
  };

  const handleSkipPositions = () => {
    setShowPositionDialog(false);
    setShowSuccessDialog(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-green-50">
        <Navbar user={user} onLogout={onLogout} />
        <div className="flex items-center justify-center h-[80vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-green-50">
      <Navbar user={user} onLogout={onLogout} />

      <main className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Header */}
        <div className="mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Geri Dön
          </Button>
          
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-3xl font-bold text-emerald-900 mb-2">
                ⚽ Maç Oluştur
              </h1>
              <p className="text-gray-600">
                Yeni bir maç düzenleyin ve oyuncu arayın
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Form */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle>Maç Bilgileri</CardTitle>
                <CardDescription>
                  Maçınızın detaylarını belirleyin
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Venue Selection */}
                  <div className="space-y-2">
                    <Label htmlFor="venue" className="flex items-center gap-2">
                      <MapPin className="h-4 w-4" />
                      Halı Saha *
                    </Label>
                    <Select
                      value={formData.venueId}
                      onValueChange={(value) => handleChange("venueId", value)}
                      required
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Saha seçin" />
                      </SelectTrigger>
                      <SelectContent>
                        {venues.map((venue) => (
                          <SelectItem key={venue.id} value={venue.id}>
                            {venue.name} - ₺{venue.pricePerHour}/saat
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {venues.length === 0 && (
                      <p className="text-sm text-gray-500">Henüz halı saha bulunmuyor</p>
                    )}
                  </div>

                  {/* Date and Time */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="date" className="flex items-center gap-2">
                        <Calendar className="h-4 w-4" />
                        Tarih *
                      </Label>
                      <Input
                        id="date"
                        type="date"
                        min={getTodayDate()}
                        value={formData.date}
                        onChange={(e) => handleChange("date", e.target.value)}
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="time" className="flex items-center gap-2">
                        <Clock className="h-4 w-4" />
                        Saat *
                      </Label>
                      <Input
                        id="time"
                        type="time"
                        value={formData.time}
                        onChange={(e) => handleChange("time", e.target.value)}
                        required
                      />
                    </div>
                  </div>

                  {/* Duration and Team Size */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="duration">Süre (dakika)</Label>
                      <Select
                        value={formData.duration.toString()}
                        onValueChange={(value) => handleChange("duration", parseInt(value))}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="60">60 dakika</SelectItem>
                          <SelectItem value="90">90 dakika</SelectItem>
                          <SelectItem value="120">120 dakika</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="teamSize">Takım Büyüklüğü</Label>
                      <Select
                        value={formData.teamSize.toString()}
                        onValueChange={(value) => {
                          const size = parseInt(value);
                          handleChange("teamSize", size);
                          handleChange("maxPlayers", size);
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="10">5v5 (10 oyuncu)</SelectItem>
                          <SelectItem value="14">7v7 (14 oyuncu)</SelectItem>
                          <SelectItem value="22">11v11 (22 oyuncu)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Price Display */}
                  {formData.venueId && (
                    <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium text-emerald-900">Tahmini Saha Ücreti:</span>
                        <span className="text-xl font-bold text-emerald-600">₺{calculatePrice()}</span>
                      </div>
                      <p className="text-xs text-emerald-700 mt-1">
                        Kişi başı yaklaşık ₺{(calculatePrice() / formData.teamSize).toFixed(2)}
                      </p>
                    </div>
                  )}

                  {/* Important Note */}
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm text-blue-800">
                      <strong>📋 Not:</strong> Maç oluşturduktan sonra saha rezervasyonu otomatik olarak yapılacaktır.
                    </p>
                  </div>

                  {/* Submit Button */}
                  <Button
                    type="submit"
                    className="w-full h-12 text-lg"
                    disabled={creating || !formData.venueId || !formData.date || !formData.time}
                  >
                    {creating ? "Oluşturuluyor..." : "Maç Oluştur"}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>

          {/* Side Info */}
          <Card className="h-fit">
            <CardHeader>
              <CardTitle className="text-lg">Maç Oluşturduktan Sonra</CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-3 text-sm">
                <li className="flex items-start gap-2">
                  <UserPlus className="h-5 w-5 text-emerald-600 mt-0.5" />
                  <span>Açık pozisyonlar için ilan ver</span>
                </li>
                <li className="flex items-start gap-2">
                  <Users className="h-5 w-5 text-emerald-600 mt-0.5" />
                  <span>Gelen başvuruları onaylayın</span>
                </li>
                <li className="flex items-start gap-2">
                  <FileText className="h-5 w-5 text-emerald-600 mt-0.5" />
                  <span>Takım kadrosunu yönetin</span>
                </li>
              </ul>
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Position Selection Dialog */}
      <Dialog open={showPositionDialog} onOpenChange={setShowPositionDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-2xl">⚽ Açık Pozisyonları Belirle</DialogTitle>
            <DialogDescription>
              Hangi pozisyonlar için oyuncu arıyorsunuz?
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>💡 İpucu:</strong> Seçtiğiniz pozisyonlar "Açık Pozisyonlar" sayfasında yayınlanacak.
              </p>
            </div>

            {/* Position Grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {[
                "Kaleci", "Defans", "Orta Saha", "Forvet", 
                "Sol Kanat", "Sağ Kanat", "Stoper", "Libero"
              ].map((position) => (
                <Button
                  key={position}
                  type="button"
                  variant={selectedPositions.includes(position) ? "default" : "outline"}
                  className={`h-16 ${selectedPositions.includes(position) ? 'bg-emerald-500 hover:bg-emerald-600' : ''}`}
                  onClick={() => {
                    if (selectedPositions.includes(position)) {
                      setSelectedPositions(selectedPositions.filter(p => p !== position));
                    } else {
                      setSelectedPositions([...selectedPositions, position]);
                    }
                  }}
                >
                  {position}
                  {selectedPositions.includes(position) && " ✓"}
                </Button>
              ))}
            </div>

            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <span className="text-sm text-gray-600">Seçili pozisyon:</span>
              <span className="font-bold text-emerald-600">{selectedPositions.length}</span>
            </div>
          </div>

          <div className="flex gap-3 mt-4">
            <Button
              type="button"
              variant="outline"
              className="flex-1"
              onClick={handleSkipPositions}
            >
              Şimdilik Atla
            </Button>
            <Button
              type="button"
              className="flex-1"
              onClick={handleConfirmPositions}
              disabled={selectedPositions.length === 0}
            >
              Pozisyonları Onayla ({selectedPositions.length})
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Success Dialog */}
      <Dialog open={showSuccessDialog} onOpenChange={setShowSuccessDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-2xl">🎉 Maç Oluşturuldu!</DialogTitle>
            <DialogDescription>
              Maçınız başarıyla oluşturuldu. Ne yapmak istersiniz?
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <Button
              className="w-full"
              onClick={() => navigate(`/matches/${createdMatchId}`)}
            >
              Maçı Görüntüle
            </Button>
            <Button
              variant="outline"
              className="w-full"
              onClick={() => navigate("/captain-dashboard")}
            >
              Kaptan Paneline Dön
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CreateMatch;